import {
  AfterViewInit,
  Component,
  DestroyRef,
  OnInit,
  ViewChild,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { PaButtonComponent } from 'src/app/shared/ui-kit/pa-button/pa-button.component';
import { PaInputOtpComponent } from 'src/app/shared/ui-kit/pa-input-otp/pa-input-otp.component';
import { environment } from 'src/environments/environment';
import { SupportLinkV2Component } from '../../components/support-link/support-link.component';
import { AuthActionsService } from '../../services/auth-actions.service';
import { AuthFlowService } from '../../services/auth-flow.service';
import { AuthSessionStore } from '../../services/auth-session.store';
import { IosKeyboardHoldService } from '../../services/ios-keyboard-hold.service';
import { LoginV2ApiService } from '../../services/login-v2-api.service';
import { classifyPvzPincodeError } from '../../helpers/pvz-pincode-error.helper';

@Component({
  selector: 'app-pvz-pincode-step',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    PaButtonComponent,
    PaInputOtpComponent,
    SupportLinkV2Component,
  ],
  templateUrl: './pvz-pincode-step.component.html',
  styleUrls: ['../../styles/_step.styles.scss'],
})
export class PvzPincodeStepComponent implements OnInit, AfterViewInit {
  readonly form = new FormGroup({
    code: new FormControl('', { nonNullable: true }),
  });

  readonly store = inject(AuthSessionStore);
  readonly environment = environment;

  private readonly flow = inject(AuthFlowService);
  private readonly actions = inject(AuthActionsService);
  private readonly loginApi = inject(LoginV2ApiService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly keyboardHold = inject(IosKeyboardHoldService);
  private isSubmitting = false;
  pinErrorText = '';

  @ViewChild(PaInputOtpComponent)
  otpInput?: PaInputOtpComponent;

  get showPinError(): boolean {
    return Boolean(this.pinErrorText);
  }

  ngOnInit(): void {
    const account = this.store.account();
    if (!account.login && !account.id) {
      this.back();
      return;
    }

    this.form.controls.code.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((code) => {
        this.store.patchAccount({ code: code ?? '' });
        if (code) {
          this.clearPinError();
        }
        if (!this.isCodeSubmitDisabled()) {
          void this.submit();
        }
      });
  }

  ngAfterViewInit(): void {
    this.focusCodeInput();
  }

  back(): void {
    this.flow.goBack();
  }

  isCodeValid(): boolean {
    return /^\d{4}$/.test(this.form.controls.code.value.trim());
  }

  isCodeSubmitDisabled(): boolean {
    return this.isSubmitting || !this.isCodeValid();
  }

  onCodeEnter(event: Event): void {
    event.preventDefault();
    if (this.isCodeSubmitDisabled()) {
      return;
    }
    void this.submit();
  }

  async submit(): Promise<void> {
    if (this.isCodeSubmitDisabled() || this.isSubmitting) {
      return;
    }

    this.isSubmitting = true;
    try {
      const result = await this.actions.confirmPincode(
        this.form.controls.code.value,
      );
      if (!result.success) {
        this.applyPincodeError(result.message, result.login_attempts);
      }
    } finally {
      this.isSubmitting = false;
    }
  }

  private applyPincodeError(
    message?: string,
    loginAttempts?: number,
  ): void {
    const view = classifyPvzPincodeError(message, loginAttempts);
    this.otpInput?.clear();

    if (!view) {
      this.focusCodeInput();
      return;
    }

    this.loginApi.toast(view.message, 'error', 'Ошибка');
    this.pinErrorText = view.showHint ? view.message : '';
    this.focusCodeInput();
  }

  private clearPinError(): void {
    this.pinErrorText = '';
  }

  private focusCodeInput(): void {
    const input = this.otpInput?.getFirstInput();
    if (input && !input.disabled) {
      this.keyboardHold.transferTo(input);
      return;
    }
    setTimeout(() => {
      const retry = this.otpInput?.getFirstInput();
      if (retry && !retry.disabled) {
        this.keyboardHold.transferTo(retry);
      }
    }, 50);
  }
}
